import React, { useLayoutEffect, useRef } from 'react';
import { fabric } from 'fabric';
import { ApplicationView, useController } from '@aha-app/mvc';
import styles from './styles.module.css';
import WhiteboardController from './controllers/WhiteboardController';
import ToolButton from './toolbars/ToolButton';
import { AttachmentPoint, ConnectorEnd, type MenuDirection } from './types';
import RouterOrthogonal from './objects/connectorRouters/RouterOrthogonal';
import type ConnectorAttachmentBehaviors from './objects/ConnectorAttachmentBehaviors';
import { STANDOFF_DISTANCE } from './utils/connectorAttachment';
import ToolbarMenu from './toolbars/ToolbarMenu';
import { type Direction } from '@aha-app/aha-base';
import classNames from 'classnames';
import type Connector from './objects/Connector';
import { isBusinessProcessShape, isShape } from './objects/guards';
import type ShapeDrawingBrush from './stylus/ShapeDrawingBrush';
import type Shape from './objects/Shape';
import { featuredShapes } from './businessProcess/definitions';
import { makeBusinessProcessItem } from './businessProcess/menuItems';
import {
  isMenuItemDefinition,
  type MenuItemDefinition,
} from './stylus/definitions';
import type StylusShape from './stylus/StylusShape';
import type EmbeddedTextBehaviors from './objects/EmbeddedTextBehaviorsV2';

// Hardcode size of menu, as we can't get the size of the menu before it appears
const MENU_SIZE = 132;
// Distance menu travels to uncover connector point offset
const CONNECTOR_POINT_OFFSET = 6;

// Center the menu on the connector, and shift it slightly so that it shows the
// connector point when in the ideal position and hides it when in default position
const getMenuPlacement = (
  { x, y }: { x: number; y: number },
  direction: 'up' | 'down' | 'left' | 'right'
): { menuDirection: Direction; xOffset: number; yOffset: number } => {
  // If there is space between the vertical/horizontal and edge of the screen
  // based on MENU_MARGIN, open the menu in that direction. Otherwise, open
  // in default direction

  const portalBox = {
    height: document.documentElement.clientHeight,
    width: document.documentElement.clientWidth,
    left: 0,
    top: 0,
  };

  let menuDirection;
  let xOffset = 0;
  let yOffset = 0;

  if (portalBox !== undefined) {
    const { left, top, width, height } = portalBox;
    const leftBound = left + MENU_SIZE / 2 > x;
    const rightBound = left + width - MENU_SIZE / 2 < x;
    const topBound = top + MENU_SIZE / 2 > y;
    const bottomBound = top + height - MENU_SIZE / 2 < y;
    let verticalXOffset;
    let horizontalYOffset;

    // xOffset when connector is vertical
    if (leftBound) {
      verticalXOffset = CONNECTOR_POINT_OFFSET;
    } else if (rightBound) {
      verticalXOffset = -CONNECTOR_POINT_OFFSET;
    } else {
      verticalXOffset = MENU_SIZE / 2;
    }

    // yOffset when connector is horizontal
    if (topBound) {
      horizontalYOffset = CONNECTOR_POINT_OFFSET;
    } else if (bottomBound) {
      horizontalYOffset = -CONNECTOR_POINT_OFFSET;
    } else {
      horizontalYOffset = MENU_SIZE / 2;
    }

    switch (direction) {
      case 'up': {
        // Each case checks the relevant constraint (in this case, the top of
        // the screen) and sets direction accordingly.
        const verticalConstraint = top + MENU_SIZE > y;
        menuDirection = `${verticalConstraint ? 'bottom' : 'top'} ${
          rightBound ? 'left' : 'right'
        }`;

        xOffset = verticalXOffset;
        yOffset = CONNECTOR_POINT_OFFSET;
        break;
      }
      case 'down': {
        const verticalConstraint = top + height - MENU_SIZE < y;
        menuDirection = `${verticalConstraint ? 'top' : 'bottom'} ${
          rightBound ? 'left' : 'right'
        }`;
        xOffset = verticalXOffset;
        yOffset = -CONNECTOR_POINT_OFFSET;
        break;
      }
      case 'right': {
        const horizontalConstraint = left + width - MENU_SIZE < x;
        menuDirection = `${horizontalConstraint ? 'left' : 'right'} ${
          bottomBound ? 'top' : 'bottom'
        }`;
        xOffset = -CONNECTOR_POINT_OFFSET;
        yOffset = horizontalYOffset;
        break;
      }
      case 'left':
      default: {
        const horizontalConstraint = left + MENU_SIZE < x;
        menuDirection = `${horizontalConstraint ? 'left' : 'right'} ${
          bottomBound ? 'top' : 'bottom'
        }`;
        xOffset = CONNECTOR_POINT_OFFSET;
        yOffset = horizontalYOffset;
        break;
      }
    }
  }

  return { menuDirection, xOffset, yOffset };
};

const adjustShapePosition = (
  point: fabric.IPoint,
  width: number,
  height: number,
  strokeWidth: number,
  direction: MenuDirection
): { left: number; top: number } => {
  const newPosition = { left: point.x, top: point.y };
  switch (direction) {
    case 'up':
      // Center the connector on the bottom of the shape
      newPosition.left -= (width + strokeWidth) / 2;
      newPosition.top -= height + STANDOFF_DISTANCE + strokeWidth;
      break;
    case 'down':
      // Center the connector on the top of the shape
      newPosition.left -= (width + strokeWidth) / 2;
      newPosition.top += STANDOFF_DISTANCE;
      break;
    case 'right':
      // Center the connector on the left side of the shape
      newPosition.left += STANDOFF_DISTANCE;
      newPosition.top -= (height + strokeWidth) / 2;
      break;
    case 'left':
    default:
      // Center the connector on the right side of the shape
      newPosition.left -= width + STANDOFF_DISTANCE + strokeWidth;
      newPosition.top -= (height + strokeWidth) / 2;
      break;
  }

  return newPosition;
};

const mapDirectionToAttachment = (
  direction: MenuDirection
): AttachmentPoint => {
  switch (direction) {
    case 'up':
      return AttachmentPoint.Bottom;
    case 'down':
      return AttachmentPoint.Top;
    case 'right':
      return AttachmentPoint.Left;
    case 'left':
      return AttachmentPoint.Right;
    default: {
      const exhaustivenessCheck: never = direction;
      return exhaustivenessCheck;
    }
  }
};

const ConnectorShapeMenu = () => {
  const controller = useController(WhiteboardController);
  const { connectorShapeMenu } = controller.state;
  const menuRef = useRef<HTMLAhaMenuElement>();

  useLayoutEffect(() => {
    if (!menuRef.current) return;

    const menu = menuRef.current;
    menu.addEventListener(
      'aha-menu:close',
      controller.actionHideConnectorShapeMenu
    );

    return () => {
      menu.removeEventListener(
        'aha-menu:close',
        controller.actionHideConnectorShapeMenu
      );
    };
  }, [menuRef, connectorShapeMenu, controller]);

  if (connectorShapeMenu) {
    // Determine the ideal opening direction for the menu based on connector
    // direction
    const connector = controller.canvas.getActiveObject() as Connector;

    // If the user presses escape while dragging, we lose active object
    if (!connector || connector.type !== 'connector') {
      controller.actionHideConnectorShapeMenu();
      return null;
    }

    const endPoint = connector.points[1];

    // Remove connector transformand offset to get canvas point
    const canvasPoint = fabric.util.transformPoint(
      endPoint as fabric.Point,
      fabric.util.invertTransform(connector.calcOwnMatrix())
    );
    canvasPoint.x += connector.pathOffset.x;
    canvasPoint.y += connector.pathOffset.y;

    // Convert to window coords for menu placement
    const windowPoint = controller.canvas.pointToWindowCoords(canvasPoint);

    if (windowPoint == null) {
      controller.actionHideConnectorShapeMenu();
      return null;
    }

    // Determine menu position and direction based on connector direction
    const direction = RouterOrthogonal.endpointDirection(
      connector,
      ConnectorEnd.CONNECTOR_END
    );
    const { menuDirection, xOffset, yOffset } = getMenuPlacement(
      windowPoint,
      direction
    );

    // Get the currently attached shape to set active shape stylus in menu
    const object = connector.attachments[0]?.object;
    const currentShape =
      object && isShape(object) ? object.shapeName : undefined;

    const options: (MenuItemDefinition | StylusShape)[] =
      object && isBusinessProcessShape(object)
        ? featuredShapes.map(makeBusinessProcessItem)
        : controller.connectorShapeMenuStyluses;

    return (
      <aha-menu
        open
        ref={menuRef}
        style={{
          position: 'absolute',
          left: windowPoint.x - xOffset,
          top: windowPoint.y - yOffset,
          width: 'unset',
          minWidth: 'unset',
        }}
        direction={menuDirection}
      >
        <div slot='control' />
        <aha-menu-content
          style={{
            '--aha-menu-content--vertical-padding': '0px',
            '--aha-menu-content--font-size': 'unset',
            padding: '5px',
            minWidth: 'unset',
          }}
        >
          <ToolbarMenu columns={3}>
            {options.map((stylusOrMenuItem, i) => (
              <span key={i}>
                <ToolButton
                  className={classNames(styles.toolButton, {
                    [styles.toolButtonActive]:
                      stylusOrMenuItem.shapeName === currentShape,
                  })}
                  key={i}
                  onClick={() => {
                    const stylus = isMenuItemDefinition(stylusOrMenuItem)
                      ? controller.getStylus(stylusOrMenuItem.stylusDefinition)
                      : stylusOrMenuItem;

                    if (
                      connector.attachments[ConnectorEnd.CONNECTOR_START] ===
                        null ||
                      connector.attachments[ConnectorEnd.CONNECTOR_END] !== null
                    ) {
                      // Only allow shape menu when connector has only a start
                      // attachment
                      return;
                    }

                    // Position the shape such that the connector is centered on
                    // the appropriate side of the shape
                    const startObj =
                      connector.attachments?.[ConnectorEnd.CONNECTOR_START]
                        ?.object;
                    const width = stylus.shapeClass.defaultWidth();
                    const height = stylus.shapeClass.defaultHeight();

                    // if this isn't a shape, these will be undefined, which is fine
                    let { strokeWidth, scaleX, scaleY } = startObj as Shape;

                    if (stylus.shapeName === 'note') {
                      strokeWidth = 0;
                    }
                    const position = adjustShapePosition(
                      canvasPoint,
                      width * scaleX,
                      height * scaleY,
                      strokeWidth,
                      direction
                    );
                    // Create the shape
                    stylus.activate();

                    (
                      controller.canvas.freeDrawingBrush as ShapeDrawingBrush
                    ).createShape({
                      drawing: false,
                      options: {
                        strokeWidth,
                        width,
                        height,
                        scaleX,
                        scaleY,
                        ...position,
                      },
                    });

                    // we won't be here unless the current object has connector behaviours
                    const shape =
                      controller.canvas.getActiveObject() as fabric.Object &
                        ConnectorAttachmentBehaviors &
                        EmbeddedTextBehaviors;

                    if (stylus.shapeName === 'text') {
                      // Need text shape to scale up to increase font size
                      shape.set({ scaleX, scaleY });
                      void shape.startTextEditing();
                    }
                    const attachmentPoint = mapDirectionToAttachment(direction);
                    shape.attachConnector(
                      connector,
                      ConnectorEnd.CONNECTOR_END,
                      attachmentPoint
                    );

                    controller.stylusPointer.activate();
                    controller.actionHideConnectorShapeMenu();
                    controller.actionShowObjectToolbar();
                    controller.canvas.requestRenderAll();
                  }}
                >
                  {stylusOrMenuItem.renderIcon()}
                </ToolButton>
              </span>
            ))}
          </ToolbarMenu>
        </aha-menu-content>
      </aha-menu>
    );
  } else {
    return null;
  }
};

export default ApplicationView(ConnectorShapeMenu);
